import { useEffect, useRef, useState } from 'react';
import { MessageSquare, PhoneIncoming, X } from 'lucide-react';
import { playMessage, startRinging } from '@ck/ui';
import { A, api, type Staff } from './api';

interface Pulse {
  waiting: { id: string; subject: string; name: string; ticket: string }[];
  invited: { id: string; subject: string; name: string; ticket: string }[];
  messages: { chat_id: string; id: string; body: string; file_name: string | null; name: string; ticket: string }[];
  calls: { chat_id: string; name: string }[];
}

/**
 * Rings like a phone only while a client is actually calling. A new waiting chat or team invite
 * chimes once and shows a banner that can be opened or dismissed; new client messages pop up briefly.
 */
const HUSH = 'ck.alarm.hushed';
const loadHushed = (): Set<string> => { try { return new Set(JSON.parse(sessionStorage.getItem(HUSH) ?? '[]')); } catch { return new Set(); } };
export function SupportAlarm({ staff, go }: { staff: Staff; go: (page: string, param?: string) => void }) {
  const [p, setP] = useState<Pulse | null>(null);
  const [pop, setPop] = useState<Pulse['messages'][number] | null>(null);
  const seen = useRef<Set<string> | null>(null);
  const known = useRef<Set<string> | null>(null);
  const [hushed, setHushed] = useState<Set<string>>(loadHushed);
  const hush = (id: string) => setHushed((h) => { const n = new Set(h).add(id); try { sessionStorage.setItem(HUSH, JSON.stringify([...n].slice(-200))); } catch { /* ignore */ } return n; });
  const on = staff.role !== 'intern';
  useEffect(() => {
    if (!on) return;
    let stop = false;
    const tick = async () => {
      try {
        const x = await api.get<Pulse>(A('/support/pulse'));
        if (stop) return;
        const ids = new Set(x.messages.map((m) => m.id));
        if (seen.current) {
          const fresh = x.messages.filter((m) => !seen.current!.has(m.id));
          if (fresh.length) { playMessage(); setPop(fresh[0]!); }
        }
        seen.current = new Set([...(seen.current ?? []), ...ids]);
        // One chime per new waiting chat or invite, never a loop.
        const waitIds = [...x.waiting, ...x.invited].map((w) => w.id);
        if (known.current && waitIds.some((id) => !known.current!.has(id))) playMessage();
        known.current = new Set([...(known.current ?? []), ...waitIds]);
        setP(x);
      } catch { /* try again next tick */ }
    };
    void tick();
    const t = window.setInterval(tick, 4000);
    return () => { stop = true; clearInterval(t); };
  }, [on]);
  const ringing = (p?.calls.length ?? 0) > 0;
  useEffect(() => { if (!ringing) return; return startRinging(3000); }, [ringing]);
  const waiting = (p?.waiting ?? []).filter((w) => !hushed.has(w.id));
  const invited = (p?.invited ?? []).filter((w) => !hushed.has(w.id));
  const urgent = ringing || waiting.length + invited.length > 0;
  useEffect(() => { if (!pop) return; const t = setTimeout(() => setPop(null), 15_000); return () => clearTimeout(t); }, [pop]);
  if (!on || (!urgent && !pop)) return null;
  const first = p?.calls[0] ? { id: p.calls[0].chat_id, call: true, label: `${p.calls[0].name} is calling` }
    : waiting[0] ? { id: waiting[0].id, call: false, label: `New chat from ${waiting[0].name}`, sub: `${waiting[0].ticket} · ${waiting[0].subject}` }
    : invited[0] ? { id: invited[0].id, call: false, label: `Your team is needed: ${invited[0].name}`, sub: invited[0].ticket } : null;
  const more = waiting.length + invited.length - (first && !first.call ? 1 : 0);
  return (
    <div className="alarm" role="alert">
      {first && (
        <div className={`alarm-ring${first.call ? '' : ' quiet'}`}>
          {first.call ? <PhoneIncoming /> : <MessageSquare />}
          <span><b>{first.label}</b>{'sub' in first && first.sub && <small>{first.sub}</small>}{more > 0 && <small>{more} more waiting</small>}</span>
          <button className="btn sm call-ok" onClick={() => { if (!first.call) hush(first.id); go('support', first.id); }}>{first.call ? 'Answer' : 'Open chat'}</button>
          {!first.call && <button className="alarm-x" aria-label="Dismiss" onClick={() => hush(first.id)}><X /></button>}
        </div>
      )}
      {pop && (
        <button className="alarm-msg" onClick={() => { go('support', pop.chat_id); setPop(null); }}>
          <b>{pop.name} · {pop.ticket}</b><small>{pop.file_name ? `Sent a file: ${pop.file_name}` : pop.body.slice(0, 160)}</small>
          <span className="alarm-x" role="button" aria-label="Dismiss" onClick={(e) => { e.stopPropagation(); setPop(null); }}><X /></span>
        </button>
      )}
    </div>
  );
}
